/**
 * @voltxampere/web — View category builtin commands (Sprint 22 C3)
 *
 * 12 commands covering zoom, grid / Bode / inspector / cursor
 * toggles, fullscreen, dark-mode, snap, label visibility. Most are
 * Sprint 23 polish stubs today — the palette discovery surface
 * surfaces the shortcuts even when the action lands later.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Command } from "../types.js";
// Sprint 80.Y L-A-2 — view.toggle-waveform-pane real handler.
import { toggleWaveformPane } from "../../store/waveform-visibility.js";
// Sprint 80.Z Phase 4 D1 — wire 3 view toggles to real handlers.
import { toggleCursors } from "../../store/waveform-cursor.js";
import { toggleHighContrast } from "../../store/a11y-state.js";

const noop = (): void => {
  /* Sprint 22 stub. */
};

/**
 * Sprint 80.Z Phase 4 D1 — `view.toggle-fullscreen` real handler.
 *
 * The DOM `Fullscreen API` provides `Document.exitFullscreen()` +
 * `Element.requestFullscreen()`. Browsers that lack the API leave
 * `document.fullscreenEnabled` falsy; the handler no-ops gracefully
 * in that case. Errors (user denied, transient nav lock) are
 * swallowed so the palette invocation never throws.
 */
function toggleFullscreen(): void {
  if (typeof document === "undefined" || !document.fullscreenEnabled) return;
  const action: Promise<void> =
    document.fullscreenElement === null
      ? document.documentElement.requestFullscreen()
      : document.exitFullscreen();
  void action.catch(() => {
    /* user denied / lock — silent */
  });
}

export const VIEW_COMMANDS: readonly Command[] = [
  {
    id: "view.zoom-in",
    titleKey: "command.view.zoomIn",
    category: "view",
    shortcut: "+",
    execute: noop, // canvas zoom controlled via Sprint 12 zoom signals; palette wires to keyboard event proxy in S23
  },
  {
    id: "view.zoom-out",
    titleKey: "command.view.zoomOut",
    category: "view",
    shortcut: "-",
    execute: noop,
  },
  {
    id: "view.zoom-fit",
    titleKey: "command.view.zoomFit",
    category: "view",
    shortcut: "0",
    execute: noop,
  },
  {
    id: "view.zoom-reset",
    titleKey: "command.view.zoomReset",
    category: "view",
    shortcut: "Cmd+1",
    execute: noop,
  },
  {
    id: "view.toggle-grid",
    titleKey: "command.view.toggleGrid",
    category: "view",
    shortcut: "G",
    execute: noop,
  },
  {
    id: "view.toggle-bode",
    titleKey: "command.view.toggleBode",
    descriptionKey: "command.view.toggleBode.desc",
    category: "view",
    execute: noop, // Sprint 21 C8 auto-mounts when AC result is set; manual toggle pending S23
  },
  {
    id: "view.toggle-inspector",
    titleKey: "command.view.toggleInspector",
    category: "view",
    execute: noop,
  },
  {
    id: "view.toggle-cursors",
    titleKey: "command.view.toggleCursors",
    category: "view",
    descriptionKey: "command.view.toggleCursors.desc",
    // Sprint 80.Z Phase 4 D1 — wired to waveform-cursor store.
    execute: toggleCursors,
  },
  {
    id: "view.toggle-fullscreen",
    titleKey: "command.view.toggleFullscreen",
    category: "view",
    shortcut: "F11",
    // Sprint 80.Z Phase 4 D1 — wired to DOM Fullscreen API.
    execute: toggleFullscreen,
  },
  {
    id: "view.toggle-darkmode",
    titleKey: "command.view.toggleDarkMode",
    category: "view",
    descriptionKey: "command.view.toggleDarkMode.desc",
    // Sprint 80.Z Phase 4 D1 — wired to a11y high-contrast.
    // The app's "dark mode" semantics map to the Sprint 80.Z B1
    // high-contrast WCAG-AAA palette (near-black bg + bright-amber
    // accents). Standalone dark-only mode is a Sprint 81+ option.
    execute: toggleHighContrast,
  },
  {
    id: "view.toggle-snap",
    titleKey: "command.view.toggleSnap",
    category: "view",
    descriptionKey: "command.view.toggleSnap.desc",
    execute: noop,
  },
  {
    id: "view.toggle-labels",
    titleKey: "command.view.toggleLabels",
    category: "view",
    execute: noop,
  },
  // Sprint 80.Y L-A-2 — osiloskop gizle/göster (Şefim #2 closure).
  // Real handler: flips waveformPaneVisible signal + persists via
  // localStorage. Surfaces in Cmd+K palette so users with the
  // shortcut overlay can find the action even if they missed the
  // chip in the topbar.
  {
    id: "view.toggle-waveform-pane",
    titleKey: "command.view.toggleWaveformPane",
    descriptionKey: "command.view.toggleWaveformPane.desc",
    category: "view",
    shortcut: "Cmd+Shift+O",
    execute: toggleWaveformPane,
  },
];
